import React, { useEffect, useState } from "react";
import { channelLogoSx, channelLogoChipFor, handleChannelLogoError } from "lib/helper/channel-logo-url";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText, { IDtvTextProps } from "@directv-digital/dds.ui.dtv-text";
import CardMedia from "@mui/material/CardMedia";
import Tooltip from "@mui/material/Tooltip";
import DtvGrid from "@directv-digital/dds.ui.dtv-grid";
import { fusionScrollbarV2 } from "@directv-digital/dds.utils";
import DtvButton, { IDtvButtonProps } from "@directv-digital/dds.ui.dtv-button";
import DtvTheme, { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";

export default interface ChannelGridProps {
  title?: IDtvTextProps; // modal title
  channels?: any;
  cta?: IDtvButtonProps; // modal bottm cta
}

const styles = {
  primaryColor: colorPalette.jet.main,
  channelGridContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    alignContent: "flex-start",
    gap: "16px 18px",
    [DtvTheme.breakpoints.down(414)]: {
      gap: "16px 5px"
    },
    alignSelf: "stretch",
    flexWrap: "wrap",
    flexGrow: 1,
    overflowY: "auto",
    ...fusionScrollbarV2()
  },
  channelLogoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    gap: "8px"
  },
  channelLogo: {
    display: "flex",
    width: {
      xs: "80px",
      sm: "88px"
    },
    height: {
      xs: "80px",
      sm: "88px"
    },
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "300px",
    // WAT-11362: contain so landscape OV logos aren't cropped (no-op for square logoUrl).
    objectFit: "contain"
  },
  tooltipStyles: {
    opacity: "1",
    transform: "none",
    transition:
      "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 133ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    transformOrigin: "left center",
    backgroundColor: "rgba(97, 97, 97, 0.92)",
    borderRadius: "4px",
    color: colorPalette.neutral_white.main,
    padding: "4px 8px",
    fontSize: "14px",
    maxWidth: "fit-content",
    margin: "0",
    wordWrap: "break-word",
    fontWeight: 400,
    fontFamily: "roboto-regular",
    position: "relative",
    marginLeft: "0px!important"
  },
  tooltipArrow: {
    position: "absolute",
    top: "0px",
    transform: "translate3d(0px, 8.8px, 0px)",
    left: "-10px",
    marginLeft: "-0.71em",
    height: "1em",
    width: "0.71em",
    overflow: "hidden",
    boxSizing: "border-box",
    color: "rgba(97, 97, 97, 0.9)",
    "::before": {
      transformOrigin: "100% 100%"
    }
  }
};

export function ChannelGrid(props: ChannelGridProps) {
  const [windowHeight, setWindowHeight] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window?.innerHeight);
    };
    handleResize();
    window?.addEventListener("resize", handleResize);
    return () => {
      window?.removeEventListener("resize", handleResize);
    };
  }, []);

  try {
    return (
      <DtvBox sx={{ height: "100%" }}>

        {/* heading */}
        <DtvBox sx={{
 position: "sticky",
top: 0,
          mb: {
 xs: "16px",
sm: "24px"
}
        }}>
          <DtvText
            content={props?.title?.toString()}
            sx={{ color: styles.primaryColor }}
            variant={"subhead1medium"}
          />
        </DtvBox>

        {/* logo grid */}
        <DtvGrid
          sx={{
            ...styles.channelGridContainer,
            height: {
              // logo grid height = windowHeight(90vh) - closeButtonHeight - headerHeight - bottomCtaHeight
              // NOTE -> any change in heading height or bottomCTA height should be subtracted here
              xs: `calc(${windowHeight * 0.9}px - 36px - 46px - 68px - 18px)`, // -18px(removing default modal bottom extra 18px)
              sm: `calc(${windowHeight * 0.9}px - 50px - 62px - 72px - 34px)`, // -34px(removing default modal bottom extra 34px)
              md: `calc(${windowHeight * 0.9}px - 58px - 68px - 80px - 34px)`,
              lg: `calc(${windowHeight * 0.9}px - 58px - 68px - 80px - 34px)`,
              xl: `calc(${windowHeight * 0.9}px - 58px - 68px - 80px - 34px)`
            }
          }}
        >
          {props?.channels?.length > 0 &&
            props?.channels?.map((channel, index) => (
                <DtvBox
                  aria-label={`${channel?.channelNameLabel} icon`}
                  id={`icon-container-${channel?.channelNameLabel}`}
                  key={index}
                  tabIndex={-1}
                  sx={{
                    ...styles.channelLogoContainer,
                  // WAT-11362: OV artwork is a bare transparent wordmark, so the white
                  // circle the legacy SVG paints itself has to come from this container.
                  ...(channel?.isOvLogo ? {
                    width: {
                      xs: "80px",
                      sm: "88px"
                    },
                    height: {
                      xs: "80px",
                      sm: "88px"
                    }
                  } : {}),
                  ...channelLogoChipFor(channel?.isOvLogo, channel?.channelLogoPath)
                  }}
                >
                  <Tooltip
                    describeChild
                    enterTouchDelay={0}
                    key={index}
                    placement="bottom"
                    tabIndex={0}
                    title={channel?.channelNameLabel}
                    PopperProps={{
                      modifiers: [
                        {
                          name: "preventOverflow",
                          enabled: true
                        },
                        {
                          name: "flip",
                          enabled: true
                        },
                        {
                          name: "offset",
                          options: {
                            offset: [0, -20]
                          }
                        }
                      ],
                      sx: { width: "unset !important" }
                    }}
                    componentsProps={{
                      tooltip: { sx: styles.tooltipStyles },
                      arrow: { sx: styles.tooltipArrow }
                    }}
                    sx={{
                      "& #popper[data-popper-escaped]": {
                        visibility: "hidden"
                      }
                    }}
                  >
                    <CardMedia
                      alt={channel?.channelNameLabel || "channel logo icon"}
                      aria-labelledby={`icon-container-${channel?.channelNameLabel}`}
                      component={"img"}
                      key={index}
                      loading="lazy"
                      src={channel?.channelLogoPath}
                      sx={{
                        ...styles.channelLogo,
                        ...channelLogoSx(channel?.isOvLogo, channel?.channelLogoPath)
                      }}
                      // WAT-11362: an OV imageURL can 404 — this surface had no handler at
                      // all, so "none" keeps the flag-off behaviour exactly as it is today.
                      onError={e => handleChannelLogoError(e, channel?.isOvLogo)}
                    />
                  </Tooltip>
                </DtvBox>
              ))}
        </DtvGrid>

        {/* cta */}
        <DtvBox
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: {
 xs: "20px",
sm: "24px",
md: "32px"
},
            mr: {
 xs: "20px",
sm: "0px"
}
          }}
        >
          <DtvButton
            googleAnalyticsData={getAnalyticsAttrs(props?.cta?.label?.toLowerCase(), 'modal_channel lineup', props?.cta?.targeturl)}
            label={props?.cta?.label}
            mode="dark"
            targeturl={props?.cta?.targeturl}
            variant="uxsecondary"
            sx={{
 height: "48px",
color: styles.primaryColor
}}
            {...props?.cta}
          />
        </DtvBox>
      </DtvBox>
    );
  } catch (error) {
    console.error(`MyFreeDirectv ChannelGrid error :: ${error}`);
    return <ErrorDisplay component="MyFreeDirectv ChannelGrid" error={error} />;
  }
}
